import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/ChatPage.css";
import Swal from "sweetalert2";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import api from "../api";
import {
  setIsChatPage,
  setSessionId as setReduxSessionId,
} from "../redux/sessionSlice";
import { branding } from "../utils/branding";

// Import the new ChatMessage component
import ChatMessage from "./ChatMessage";
import { useOutletContext } from "react-router-dom";

import fileIcon from "../assets/UserIcons/FileIcon.svg";
import send from "../assets/UserIcons/SendChatIcon.svg";
import crossIcon from "../assets/UserIcons/Cancel.svg";
import { truncate, validateFileName } from "../utils/validateFileName";

import VelaKoda from "../assets/Images/LoginLogo.svg";
import customizeResIcon from "../assets/Images/Filter icon.svg";
import reloadIcon from "../assets/UserIcons/Reload.svg";

// Utility function for debouncing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const normalizeModelType = (value, fallback) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "online" || normalized === "offline") return normalized;
  return fallback;
};

const ChatPage = ({
  firstMessage,
  attachedFilesFromHome,
  profilePic,
  userName,
  // isSearchActive,
  // isKnowledgeSearchActive,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { selectedModel, selectedMode } = useSelector((state) => state.model);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const chatEndRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const hasInteractedRef = useRef(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isBotResponding, setIsBotResponding] = useState(false);
  const typingIntervalRef = useRef(null);
  const [shouldForceScroll, setShouldForceScroll] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const searchTerm = useSelector((state) => state.chat.searchTerm);
  const [micOpen, setMicOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [tempTranscript, setTempTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const currentUtteranceRef = useRef(null);

  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  const { isChatPage } = useSelector((state) => state.session);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isProcessingSTT, setIsProcessingSTT] = useState(false);
  const [activeOption, setActiveOption] = useState(null);
  const location = useLocation();
  const [showResponsesModal, setShowResponsesModal] = useState(false);

  const [chatPreferences, setChatPreferences] = useState({
    font_size: "Medium",
    auto_scroll: true,
    bot_language: "English",
  });

  const fontSizeMap = {
    Small: "0.75rem", // 12px ✅
    Medium: "0.8rem", // 12.8px ✅ (still below 13)
    Large: "0.8125rem", // 13px (borderline – use only if needed)
  };

  const [activeButton, setActiveButton] = useState(""); // store "attach", "read", "mic", "search"
  // const [searchActive, setIsSearchActive] = useState(isSearchActive ?? true);
  const {
    isSearchActive,
    isKnowledgeSearchActive,
    setIsKnowledgeSearchActive,
    uploadedDocNames,
    setUploadedDocNames,
    selectedGroups,
    selectedFiles,
    setSelectedFiles,
    chatResetToken,
    aiPreferences,
    setAiPreferences,
    resetAiPreferences,
    DEFAULT_AI_PREFERENCES,
  } = useOutletContext();

  // console.log("uploadedDocNames", uploadedDocNames);

  const selectedModelRef = useRef(selectedModel);
  const isTypingRef = useRef(false);

  const botLifecycleRef = useRef({
    status: "idle", // idle | requesting | typing
  });

  const botStartTimeRef = useRef(null);
  const skipHistoryFetchRef = useRef(false);
  const recoveryAttemptRef = useRef(0);
  const recoveryTimerRef = useRef(null);
  const activeResponseRef = useRef({
    botTypingId: null,
    chatId: null,
    fullResponse: "",
    charIndex: 0,
  });

  const setBotStatus = (status) => {
    botLifecycleRef.current.status = status;
  };

  const isBotBusy = () => botLifecycleRef.current.status !== "idle";

  const botBusy = isBotBusy();

  const clearRecovery = useCallback(() => {
    recoveryAttemptRef.current += 1;
    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }

    activeResponseRef.current = {
      botTypingId: null,
      chatId: null,
      fullResponse: "",
      charIndex: 0,
    };
  }, []);

  const makeClientMessageId = useCallback(() => {
    try {
      if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
    } catch {}

    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }, []);

  useEffect(() => {
    selectedModelRef.current = selectedModel;
  }, [selectedModel]);

  useEffect(() => {
    // Clear docs when route or session changes
    setUploadedDocNames([]);
  }, [sessionId, location.pathname]);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const [user, setUser] = useState({
    user_profile_pic: "",
    name: "",
    email: "",
  });
  const [loading, setLoading] = useState(true);
  const refreshKey = useSelector((state) => state.refresh.key);

  // 🔑 Get privacy mode from Redux
  const privacy_mode = useSelector((state) => state.privacy.isFullPrivacy);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchSessionHistory = useCallback(
    async (targetSessionId, { silent = false } = {}) => {
      if (!targetSessionId) return null;

      try {
        const response = await api.get(`/chat/session/${targetSessionId}`);
        const data = response?.data;

        if (!Array.isArray(data.history)) return [];

        return data.history.map((msg) => ({
          id: msg.id,
          sender: msg.sender,
          message: msg.message,
          time: msg.time,
          userName: msg.userName || "User",
          userPic: msg.userPic || null,

          // ✅ MODEL INFO
          model_name: msg.model_name || null,
          model_generic_name: msg.model_generic_name || msg.model_name || null,
          model_type: msg.model_type || "online",

          // 🔹 METRICS
          response_time: msg.response_time ?? null,
          response_record_time: msg.response_record_time ?? null,
          input_tokens: msg.input_tokens ?? null,
          output_tokens: msg.output_tokens ?? null,

          // ✅ FILE METADATA
          files:
            msg.files?.map((f) => ({
              name: f.name,
              size: f.size,
              type: f.type,
              url: f.url || null,
            })) || [],

          // ✅ RAG SOURCES & CITATIONS
          citations: msg.citations || [],
          rag_sources: msg.rag_sources || [],
          follow_up: msg.follow_up || [],
          bot_generated_file: msg.bot_generated_file || null,
        }));
      } catch (err) {
        const status = err?.response?.status;
        console.error("Error:", err);

        if (status === 401) {
          if (!silent) {
            setModalMessage("Your session has expired. Please login again.");
            setShowModal(true);
          }
          return null;
        }

        if (!silent) {
          setModalMessage("Failed to load chat history. Please try again.");
          setShowModal(true);
        }
        return null;
      }
    },
    [api],
  );

  const ensureSessionForSend = useCallback(
    async (modelId) => {
      if (sessionId) return sessionId;
      if (!modelId) return null;
      if (!token) return null;

      const knowledgeEnabled = Boolean(
        isKnowledgeSearchActive ||
          selectedGroups.length > 0 ||
          selectedFiles.length > 0,
      );

      try {
        const res = await api.post(
          `${process.env.REACT_APP_API_URL}/chat/new-session`,
          {
            model_id: modelId,
            mode: selectedMode,
            knowledge_search_enabled: knowledgeEnabled,
            group_ids: selectedGroups,
            knowledge_base_files: selectedFiles,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const newSessionId = res.data?.session_id;
        if (!newSessionId) throw new Error("Missing session_id");

        skipHistoryFetchRef.current = true;
        setSessionId(newSessionId);
        dispatch(setReduxSessionId(newSessionId));
        dispatch(setRefreshSidebar(true));
        navigate("/user/new", {
          state: { sessionId: newSessionId },
          replace: true,
        });

        return newSessionId;
      } catch (err) {
        console.error("Failed to create session:", err);
        return null;
      }
    },
    [
      dispatch,
      isKnowledgeSearchActive,
      navigate,
      selectedGroups,
      selectedFiles,
      selectedMode,
      sessionId,
      token,
    ],
  );

  const recoverBotResponse = useCallback(
    async ({
      targetSessionId,
      userText,
      botTypingId,
      startedAt,
      maxMs = 600000,
    }) => {
      if (!targetSessionId) return;

      const attemptId = ++recoveryAttemptRef.current;
      const start = Date.now();

      setBotStatus("requesting");
      setIsBotResponding(true);

      let delayMs = 2000;

      const pollOnce = async () => {
        if (recoveryAttemptRef.current !== attemptId) return;

        const formatted = await fetchSessionHistory(targetSessionId, {
          silent: true,
        });

        if (recoveryAttemptRef.current !== attemptId) return;

        if (Array.isArray(formatted) && formatted.length > 0) {
          const normalizedUserText = String(userText ?? "").trim();
          const lastUserIndex = normalizedUserText
            ? formatted
                .map((m) =>
                  m.sender === "user"
                    ? String(m.message ?? "").trim()
                    : null,
                )
                .lastIndexOf(normalizedUserText)
            : -1;

          const candidate =
            lastUserIndex >= 0 ? formatted[lastUserIndex + 1] : null;

          if (
            candidate &&
            candidate.sender === "bot" &&
            String(candidate.message ?? "").trim().length > 0
          ) {
            clearRecovery();
            setMessages(formatted);
            setIsBotResponding(false);
            setBotStatus("idle");
            dispatch(setRefreshSidebar(true));
            return;
          }
        }

        const elapsed = Date.now() - start;
        if (elapsed >= maxMs) {
          clearRecovery();
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message:
                      "This is taking longer than usual. The server may still be generating the response—please wait a bit more or retry.",
                    partialMessage: "",
                    showDots: false,
                    response_record_time:
                      msg.response_record_time ??
                      (
                        (Date.now() -
                          (startedAt || msg.startedAt || Date.now())) /
                        1000
                      ).toFixed(2),
                  }
                : msg,
            ),
          );
          setIsBotResponding(false);
          setBotStatus("idle");
          dispatch(setRefreshSidebar(true));
          return;
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId
              ? {
                  ...msg,
                  message:
                    msg.message ||
                    "Connection issue detected. Trying to fetch the response…",
                  partialMessage: "",
                  showDots: true,
                }
              : msg,
          ),
        );

        // Backoff polling to reduce load while still auto-updating.
        delayMs = Math.min(Math.floor(delayMs * 1.4), 10000);
        recoveryTimerRef.current = setTimeout(pollOnce, delayMs);
      };

      pollOnce();
    },
    [dispatch, fetchSessionHistory, clearRecovery],
  );

  const toggleKbFile = useCallback(
    (source) => {
      const normalize = (v) => String(v ?? "").trim();
      const key = (v) => normalize(v).toLowerCase();
      const filename = normalize(source?.filename);
      // eslint-disable-next-line no-console
      console.log("[KB] toggleKbFile called", {
        filename,
        hasSetSelectedFiles: typeof setSelectedFiles === "function",
        hasSetIsKnowledgeSearchActive:
          typeof setIsKnowledgeSearchActive === "function",
      });
      if (!filename) return;

      setSelectedFiles((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const targetKey = key(filename);
        const map = new Map();
        for (const v of prevArr) {
          const k = key(v);
          if (!k) continue;
          if (!map.has(k)) map.set(k, normalize(v));
        }
        const had = map.has(targetKey);
        if (had) map.delete(targetKey);
        else map.set(targetKey, filename);
        const next = Array.from(map.values());

        // eslint-disable-next-line no-console
        console.log("[KB] toggle file", { filename, had, next });

        return next;
      });
    },
    [
      setSelectedFiles,
    ],
  );

  const createNewChatFromKb = useCallback(async () => {
    if (!selectedModel) {
      showErrorToast("Select a model first");
      return;
    }

    const knowledgeEnabled = Boolean(
      isKnowledgeSearchActive ||
        selectedGroups.length > 0 ||
        selectedFiles.length > 0,
    );

    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/chat/new-session`,
        {
          model_id: selectedModel,
          mode: selectedMode,
          knowledge_search_enabled: knowledgeEnabled,
          group_ids: selectedGroups,
          knowledge_base_files: selectedFiles,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const newSessionId = res.data?.session_id;
      if (!newSessionId) throw new Error("Missing session_id");

      try {
        sessionStorage.setItem("kb_auto_open_dropdown", "1");
        sessionStorage.setItem("kb_keep_selection_for_next_session", "1");
      } catch {}

      setMessages([]);
      setInput("");
      setAttachedFiles([]);
      setUploadedDocNames([]);
      setEditingId(null);
      setIsBotResponding(false);
      setBotStatus("idle");

      setSessionId(newSessionId);
      dispatch(setReduxSessionId(newSessionId));
      dispatch(setRefreshSidebar(true));
      navigate("/user/new", {
        state: { sessionId: newSessionId },
        replace: true,
      });
    } catch (err) {
      console.error("Failed to create session:", err);
      showErrorToast("Failed to create session");
    }
  }, [
    dispatch,
    isKnowledgeSearchActive,
    navigate,
    selectedGroups,
    selectedFiles,
    selectedMode,
    selectedModel,
    setUploadedDocNames,
    token,
  ]);

  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [isDragging, setIsDragging] = useState(false);

  const hasEmailExtractFiles = useMemo(() => {
    const getExt = (name) => name?.split(".").pop()?.toLowerCase();
    return attachedFiles.some((f) => {
      const ext = getExt(f?.file?.name);
      return ext === "zip" || ext === "msg";
    });
  }, [attachedFiles]);

  useEffect(() => {
    if (!hasEmailExtractFiles) return;
    if (!input) return;
    setInput("");
    const textarea = document.querySelector(".input-textarea");
    if (textarea) {
      textarea.style.height = "40px";
      textarea.style.overflowY = "hidden";
    }
  }, [hasEmailExtractFiles, input]);

  const storedUser =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));

  const profilePicPath =
    storedUser?.user_profile_pic || user?.user_profile_pic || "";

  const loggedInPic = profilePicPath
    ? `${process.env.REACT_APP_SERVER_URL}/${String(profilePicPath).replace(
        /\\/g,
        "/",
      )}`
    : `${process.env.REACT_APP_SERVER_URL}/uploads/default_img.png`;

  const stripMarkdown = (text = "") => {
    return (
      text
        // headers ### ##
        .replace(/^#{1,6}\s+/gm, "")
        // bold / italic *** ** *
        .replace(/\*\*\*([^*]+)\*\*\*/g, "$1")
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/\*([^*]+)\*/g, "$1")
        // inline code `
        .replace(/`([^`]+)`/g, "$1")
        // code blocks ```
        .replace(/```[\s\S]*?```/g, "")
        // links [text](url)
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        // extra newlines
        .replace(/\n{2,}/g, "\n")
        .trim()
    );
  };

  useEffect(() => {
    if (!sessionId) return;
    // setUploadedDocNames([]);

    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  }, [sessionId]);

  const handleTextToSpeech = useCallback((text) => {
    if (!text) return;

    // 🔹 Convert markdown to plain text for TTS
    const cleanText = stripMarkdown(text);

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();

      if (
        currentUtteranceRef.current &&
        currentUtteranceRef.current.text === cleanText
      ) {
        currentUtteranceRef.current = null;
        return;
      }
    }

    const utterance = new SpeechSynthesisUtterance(cleanText);
    currentUtteranceRef.current = utterance;

    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onend = () => {
      currentUtteranceRef.current = null;
    };

    window.speechSynthesis.speak(utterance);
  }, []);


  const getBotGeneratedFileMeta = (botData = {}) => {
    const fromPayload = botData?.bot_generated_file;
    if (fromPayload?.url) return fromPayload;

    const apiPayload =
      botData?.fastapi_response && typeof botData.fastapi_response === "object"
        ? botData.fastapi_response
        : botData;

    const fileObj =
      apiPayload?.file && typeof apiPayload.file === "object"
        ? apiPayload.file
        : null;

    const fileUrl = fileObj?.url || apiPayload?.file_path;
    if (!fileUrl || typeof fileUrl !== "string") return null;

    const explicitName = fileObj?.filename || null;
    const explicitSize = Number.isFinite(Number(fileObj?.file_size))
      ? Number(fileObj.file_size)
      : null;
    const explicitType = fileObj?.file_extension
      ? String(fileObj.file_extension).toLowerCase()
      : null;

    try {
      const parsed = new URL(fileUrl);
      const rawName = decodeURIComponent(
        parsed.pathname.split("/").pop() || "generated_file",
      );
      const cleanName = rawName.replace(/\s+/g, " ").trim() || "generated_file";
      const inferredType = cleanName.includes(".")
        ? cleanName.split(".").pop().toLowerCase()
        : null;

      return {
        url: fileUrl,
        name: explicitName || cleanName,
        size: explicitSize,
        type: explicitType || inferredType,
        description: apiPayload?.ppt_description || null,
      };
    } catch {
      return {
        url: fileUrl,
        name: explicitName || "generated_file",
        size: explicitSize,
        type: explicitType,
        description: apiPayload?.ppt_description || null,
      };
    }
  };
  const updateResponseRecordTime = async (chatId, seconds) => {
    try {
      await api.post(
        "/chat/update-response-time",
        {
          chat_id: chatId,
          response_record_time: seconds,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (err) {
      console.error("Failed to update response time", err);
    }
  };

  // ============================================================================
  // OPTIMIZED TYPING ANIMATION - Uses functional updates
  // ============================================================================

  const startTypingAnimation = useCallback(
    (fullResponse, botTypingId, botData) => {
      activeResponseRef.current = {
        botTypingId,
        chatId: botData?.chat_id || botData?._id || null,
        fullResponse: String(fullResponse || ""),
        charIndex: 0,
      };

      const generatedFileMeta = getBotGeneratedFileMeta(botData);

      // Show generated attachment immediately, before typed response text.
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botTypingId
            ? {
                ...msg,
                bot_generated_file: generatedFileMeta,
              }
            : msg,
        ),
      );

      const getTypingConfig = (len) => {
        if (len > 2000) return { delay: 16, chunk: 8 };
        if (len > 1000) return { delay: 20, chunk: 6 };
        if (len > 500) return { delay: 25, chunk: 4 };
        return { delay: 30, chunk: 2 };
      };

      isTypingRef.current = true;
      setBotStatus("requesting");

      let charIndex = 0;

      // ✅ renamed for clarity (first token logic)
      let firstTokenCaptured = false;

      const { delay, chunk } = getTypingConfig(fullResponse.length);

      typingIntervalRef.current = setInterval(() => {
        charIndex = Math.min(charIndex + chunk, fullResponse.length);
        activeResponseRef.current.charIndex = charIndex;

        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== botTypingId) return msg;

            let updatedMsg = {
              ...msg,
              partialMessage: fullResponse.slice(0, charIndex),
              showDots: false,
            };

            // 🧠 STOP TIMER ON FIRST TOKEN (ONCE ONLY)
            if (!firstTokenCaptured && charIndex > 0 && msg.startedAt) {
              firstTokenCaptured = true;

              const totalSeconds = (
                (Date.now() - msg.startedAt) /
                1000
              ).toFixed(2);

              // ✅ freeze thinking time
              updatedMsg.response_record_time = totalSeconds;

              // 👉 optional backend sync
              updateResponseRecordTime(botData.chat_id, totalSeconds);
            }

            return updatedMsg;
          }),
        );

        // 🔚 FINAL RESPONSE (typing complete)
        if (charIndex >= fullResponse.length) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          activeResponseRef.current = {
            botTypingId: null,
            chatId: null,
            fullResponse: "",
            charIndex: 0,
          };

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message: fullResponse,
                    partialMessage: "",
                    showDots: false,
                    citations: botData.citations || [],
                    rag_sources: botData.rag_sources || [],
                    follow_up: botData.follow_up || [],
                    model_name: botData.model_name,
                    model_generic_name: botData.model_generic_name,
                    model_type: normalizeModelType(
                      botData.model_type ?? botData.session_type,
                      privacy_mode ? "offline" : "online",
                    ),
                    response_record_time: msg.response_record_time ?? null, // preserve recorded time
                    // 🔹 METRICS (unchanged)
                    response_time: botData.response_time ?? null,
                    // response_record_time: botData.response_record_time ?? null,
                    input_tokens: botData.input_tokens ?? null,
                    output_tokens: botData.output_tokens ?? null,
                    bot_generated_file: generatedFileMeta,
                  }
                : msg,
            ),
          );

          isTypingRef.current = false;
          setIsBotResponding(false);
          setBotStatus("idle");
        }
      }, delay);
    },
    [privacy_mode],
  );

  const recoverBotResponseByChatId = useCallback(
    async ({
      chatId,
      targetSessionId,
      userText,
      botTypingId,
      startedAt,
      maxMs = 600000,
    }) => {
      if (!chatId) {
        if (targetSessionId) {
          recoverBotResponse({
            targetSessionId,
            userText,
            botTypingId,
            startedAt,
            maxMs,
          });
        }
        return;
      }

      const attemptId = ++recoveryAttemptRef.current;
      const start = Date.now();

      setBotStatus("requesting");
      setIsBotResponding(true);

      let delayMs = 1200;

      const pollOnce = async () => {
        if (recoveryAttemptRef.current !== attemptId) return;

        try {
          const res = await api.get(`/chat/result/${chatId}`);

          if (recoveryAttemptRef.current !== attemptId) return;

          const data = res?.data || {};
          const status = String(data.response_status || "").toLowerCase();

          if (status === "success") {
            clearRecovery();
            dispatch(setRefreshSidebar(true));
            startTypingAnimation(
              data.response ||
                data.message ||
                "Apologies, there was a temporary issue processing your request.",
              botTypingId,
              data,
            );
            return;
          }

          if (status && status !== "in progress") {
            clearRecovery();
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === botTypingId
                  ? {
                      ...msg,
                      message:
                        data.response ||
                        data.message ||
                        "We apologize for the inconvenience. We’re temporarily unable to process your request due to a service issue on our end. Please try again soon.",
                      partialMessage: "",
                      showDots: false,
                      response_record_time:
                        msg.response_record_time ??
                        (
                          (Date.now() -
                            (startedAt || msg.startedAt || Date.now())) /
                          1000
                        ).toFixed(2),
                    }
                  : msg,
              ),
            );
            setIsBotResponding(false);
            setBotStatus("idle");
            dispatch(setRefreshSidebar(true));
            return;
          }
        } catch (err) {
          if (recoveryAttemptRef.current !== attemptId) return;
          const status = err?.response?.status;
          if (status === 401) {
            clearRecovery();
            setModalMessage("Your session has expired. Please login again.");
            setShowModal(true);
            setIsBotResponding(false);
            setBotStatus("idle");
            return;
          }
          // If the job API isn't available / fails, fall back to session-history recovery.
          if (targetSessionId) {
            recoverBotResponse({
              targetSessionId,
              userText,
              botTypingId,
              startedAt,
              maxMs,
            });
            return;
          }
        }

        const elapsed = Date.now() - start;
        if (elapsed >= maxMs) {
          clearRecovery();
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message:
                      "This is taking longer than usual. The server may still be generating the response—please wait a bit more or retry.",
                    partialMessage: "",
                    showDots: false,
                    response_record_time:
                      msg.response_record_time ??
                      (
                        (Date.now() -
                          (startedAt || msg.startedAt || Date.now())) /
                        1000
                      ).toFixed(2),
                  }
                : msg,
            ),
          );
          setIsBotResponding(false);
          setBotStatus("idle");
          dispatch(setRefreshSidebar(true));
          return;
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId
              ? {
                  ...msg,
                  message:
                    msg.message ||
                    "Connection issue detected. Trying to fetch the response…",
                  partialMessage: "",
                  showDots: true,
                }
              : msg,
          ),
        );

        delayMs = Math.min(Math.floor(delayMs * 1.35), 10000);
        recoveryTimerRef.current = setTimeout(pollOnce, delayMs);
      };

      pollOnce();
    },
    [clearRecovery, dispatch, recoverBotResponse, startTypingAnimation, token],
  );

  const updateBotMessage = useCallback((id, updates) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg)),
    );
  }, []);

  // ============================================================================
  // DEBOUNCED SCROLL HANDLER
  // ============================================================================

  const debouncedScrollCheck = useCallback(
    debounce(() => {
      const chatBox = document.querySelector(".chat-box");
      if (!chatBox) return;

      const atBottom =
        chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;
      setShowScrollDown(!atBottom);
    }, 100),
    [],
  );

  // ============================================================================
  // FILTERED MESSAGES - Memoized to prevent recalculation
  // ============================================================================

  const filteredMessages = useMemo(() => {
    return messages.filter((msg) =>
      msg.message?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [messages, searchTerm]);

  // ============================================================================
  // RECORDING HANDLERS
  // ============================================================================

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      setIsRecording(true);
      setInterimTranscript("");
      setTempTranscript("");

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
    } catch (err) {
      console.error("Mic error:", err);
      showErrorToast("Microphone access denied");
    }
  };

  const stopRecording = async () => {
    setIsRecording(false);

    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.stop();

    recorder.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });

      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");

      try {
        // 🔄 START PROCESSING
        setIsProcessingSTT(true);
        setInterimTranscript("Processing...");

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/chat/realtime-stt`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("STT request failed");
        }

        const data = await response.json();
        const text = data?.transcription?.trim();

        if (text) {
          setInput((prev) => prev + text + " ");
        }
      } catch (err) {
        console.error("STT error:", err);
        showErrorToast("Speech recognition failed");
      } finally {
        // ✅ STOP PROCESSING
        setIsProcessingSTT(false);
        setMicOpen(false);
        setInterimTranscript("");
        setTempTranscript("");
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
      }
    };
  };

  const cancelRecording = () => {
    setIsRecording(false);

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    audioChunksRef.current = [];
    setMicOpen(false);
    setInterimTranscript("");
    setTempTranscript("");
  };

  const toggleReadAloud = useCallback(() => {
    if (!input) return;
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      const utterance = new SpeechSynthesisUtterance(input);
      utterance.lang = "en-US";
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
    }
  }, [input]);

  // ============================================================================
  // FILE HANDLERS
  // ============================================================================

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Allowed extensions
  const allowedExtensions = [
    "pdf",
    "docx",
    "txt",
    "doc",
    "pptx",
    "xlsx",
    "zip",
    "msg",
    "mp3",
    "wav",
    "aac",
    "flac",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "flv",
    "png",
    "jpg",
    "jpeg",
    "gif",
    "bmp",
    "tiff",
  ];

  const filterSupportedFiles = (files) => {
    const supported = [];
    const unsupported = [];

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (allowedExtensions.includes(ext)) {
        supported.push(file);
      } else {
        unsupported.push(file);
      }
    });

    return { supported, unsupported };
  };

  // Handle file selection new
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const { supported, unsupported } = filterSupportedFiles(files);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (supported.length === 0) return;

    // 1️⃣ Validate filenames (DO NOT HARD BLOCK)
    const validNameFiles = [];
    const invalidNameFiles = [];
    const normalize = (s) => s?.trim().toLowerCase();

    const extractDuplicateNames = (duplicates = []) =>
      duplicates.map((d) => normalize(d.originalname || d.filename));

    for (const file of supported) {
      const { valid, reason } = validateFileName(file.name);

      if (valid) {
        validNameFiles.push(file);
      } else {
        invalidNameFiles.push({ name: file.name, reason });
      }
    }

    if (invalidNameFiles.length > 0) {
      const count = invalidNameFiles.length;

      // 🧠 Adaptive timing
      const autoCloseTime = count <= 2 ? 2000 : count <= 4 ? 1600 : 1200;

      const gap = autoCloseTime + 150;

      invalidNameFiles.forEach((f, index) => {
        setTimeout(() => {
          showErrorToast(`${f.reason}${truncate(f.name)}`, {
            autoClose: autoCloseTime,
          });
        }, index * gap);
      });
    }

    if (validNameFiles.length === 0) {
      e.target.value = "";
      return;
    }

    // 2️⃣ Optimistically add files as "uploading"
    const getExt = (name) => name?.split(".").pop()?.toLowerCase();
    const isEmailExtractFile = (file) => {
      const ext = getExt(file?.name);
      return ext === "zip" || ext === "msg";
    };

    // Split: email-extract files (.zip/.msg) should NOT be uploaded to library
    const emailFiles = validNameFiles.filter(isEmailExtractFile);
    const libraryFiles = validNameFiles.filter((f) => !isEmailExtractFile(f));

    // Enforce: only one zip total (existing + newly selected)
    const existingZipCount = attachedFiles.filter(
      (f) => getExt(f?.file?.name) === "zip",
    ).length;
    const newZipFiles = emailFiles.filter((f) => getExt(f?.name) === "zip");

    let emailFilesAllowed = emailFiles;
    if (existingZipCount > 0 && newZipFiles.length > 0) {
      showErrorToast("Only one .zip file is allowed.");
      emailFilesAllowed = emailFilesAllowed.filter(
        (f) => getExt(f?.name) !== "zip",
      );
    } else if (newZipFiles.length > 1) {
      showErrorToast("Only one .zip file is allowed.");
      const [firstZip] = newZipFiles;
      emailFilesAllowed = [
        ...emailFilesAllowed.filter((f) => getExt(f?.name) !== "zip"),
        firstZip,
      ];
    }

    const emailTempFiles = emailFilesAllowed.map((file) => ({
      file,
      status: "done",
      clientId: `${file.name}-${file.size}-${file.lastModified}`,
    }));

    const tempFiles = libraryFiles.map((file) => ({
      file,
      status: "uploading",
      clientId: `${file.name}-${file.size}-${file.lastModified}`,
    }));

    setAttachedFiles((prev) => [...prev, ...emailTempFiles, ...tempFiles]);

    if (emailTempFiles.length > 0) {
      setInput("");
      const textarea = document.querySelector(".input-textarea");
      if (textarea) {
        textarea.style.height = "40px";
        textarea.style.overflowY = "hidden";
      }
    }

    if (libraryFiles.length === 0) {
      e.target.value = "";
      return;
    }

    const selectedNames = libraryFiles.map((f) => normalize(f.name));

    setUploadedDocNames((prev) => [...new Set([...prev, ...selectedNames])]);
    // 3️⃣ Prepare form data
    const formData = new FormData();
    libraryFiles.forEach((file) => formData.append("files", file));
    formData.append("model", selectedModel);
    formData.append("mode", "personal");

    try {
      const token =
        localStorage.getItem("access_token") ||
        sessionStorage.getItem("access_token");

      const uploadEndpoint = `${process.env.REACT_APP_API_URL}/uploaded-files/newQueryUpload`;

      // 4️⃣ Upload files
      const res = await api.post(uploadEndpoint, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // ✅ NEW BACKEND CONTRACT
      const { toasts = [] } = res.data;
      // console.log("🔥 Toasts received:", toasts.length, toasts);

      if (toasts.length > 0) {
        const count = toasts.length;

        // 🧠 Adaptive timing (same philosophy as filename validation)
        const autoCloseTime = count <= 2 ? 2200 : count <= 4 ? 1800 : 1400;

        const gap = autoCloseTime + 150;

        toasts.forEach((toast, index) => {
          setTimeout(() => {
            const options = {
              autoClose: autoCloseTime,
              toastId: `upload-${index}-${toast.message}`,
            };

            const message = formatToastMessage(toast.message);

            if (toast.type === "success") {
              showSuccessToast(message, options);
            } else if (toast.type === "warning") {
              (showErrorToast || showErrorToast)(message, options);
            } else {
              showErrorToast(message, options);
            }
          }, index * gap);
        });
      }

      // 2️⃣ Update UI statuses based on toast messages
      setAttachedFiles((prev) =>
        prev.map((f) => {
          const fileName = normalize(f.file.name);

          const success = toasts.find(
            (t) =>
              t.type === "success" &&
              t.message.toLowerCase().includes(f.file.name.toLowerCase()),
          );

          const duplicate = toasts.find(
            (t) =>
              t.message.toLowerCase().includes(f.file.name.toLowerCase()) &&
              (t.message.includes("already exists") ||
                t.message.includes("pending")),
          );

          const failed = toasts.find(
            (t) =>
              t.type === "error" &&
              t.message.toLowerCase().includes(f.file.name.toLowerCase()),
          );

          if (success) return { ...f, status: "done" };
          if (duplicate) return { ...f, status: "already_trained" };
          if (failed) return { ...f, status: "error" };

          return f;
        }),
      );
    } catch (error) {
      const status = error.response?.status;
      const backendMessage =
        error.response?.data?.message || error.response?.data?.error;

      console.error("Upload failed:", {
        status,
        backendMessage,
        error,
      });

      // 🟡 ONLY mark already_trained if backend explicitly says duplicate
      const isDuplicate =
        backendMessage &&
        backendMessage.toLowerCase().includes("already exists");

      // 🔴 BLOCKED BY BACKEND (Global library conflict)
      if (isDuplicate) {
        showSuccessToast(backendMessage);

        // 🚨 STOP SPINNER — mark uploading files as error
        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.status === "uploading" ? { ...f, status: "already_trained" } : f,
          ),
        );
      } else {
        // 🔴 REAL UNKNOWN ERROR
        console.error("Upload failed:", error);

        showErrorToast(
          backendMessage || "Upload failed. Please try again later.",
        );

        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.status === "uploading" ? { ...f, status: "error" } : f,
          ),
        );
      }
    }

    // 🔟 Reset file input
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;

    // Reuse existing logic
    handleFileChange({ target: { files } });
  };

  const removeAttachedFile = useCallback((index) => {
    setAttachedFiles((prev) => {
      const fileToRemove = prev[index]?.file;
      const removedName = fileToRemove?.name?.trim().toLowerCase();

      // update uploadedDocNames safely
      if (removedName) {
        setUploadedDocNames((docs) =>
          docs.filter((name) => name !== removedName),
        );
      }

      // remove file from UI list
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleCancelq = useCallback(() => {
    setErrorModal({ show: false, message: "" });
    setAttachedFiles([]);
  }, []);

  const handleOk = useCallback(() => {
    setErrorModal({ show: false, message: "" });
    navigate("/user/chat/document");
  }, [navigate]);

  const formatToastMessage = (message) => {
    if (!message) return message;

    // Try to detect filenames with extensions
    const fileRegex = /([\w,\s-]+\.[A-Za-z]{2,6})/g;

    return message.replace(fileRegex, (match) => truncate(match));
  };

  // ============================================================================
  // MAIN SEND HANDLER
  // ============================================================================

  const handleSend = useCallback(
    async (customInput, filesParam = null) => {
      let finalInput = customInput ?? input;

      const modelToUse = selectedModelRef.current;

      if (!modelToUse) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "warning",
          title: "Please select a model before sending a message.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }

      // console.log("Selected Model", modelToUse);

      const filesToUse = filesParam ?? attachedFiles;

      const getExt = (name) => name?.split(".").pop()?.toLowerCase();
      const doneFiles = filesToUse.filter((f) =>
        ["done", "already_trained"].includes(f.status),
      );
      const emailFiles = doneFiles.filter((f) => {
        const ext = getExt(f?.file?.name);
        return ext === "zip" || ext === "msg";
      });

      if (!finalInput.trim() && emailFiles.length > 0) {
        finalInput = "Files uploaded for expense email processing.";
      }

      const attached_document = filesToUse
        .filter((f) => ["done", "already_trained"].includes(f.status))
        .map((f) => ({
          name: f.file.name,
          size: f.file.size,
          type: f.file.type || getExt(f.file.name) || "unknown",
        }));

      if (!finalInput.trim() && filesToUse.length === 0) return;
      if (!finalInput.trim()) return;

      if (!token) {
        Swal.fire({
          icon: "error",
          title: "Unauthorized",
          text: "Please login again.",
        });
        return;
      }

      clearRecovery();

      // Ensure we have a session id so we can recover from request drops by
      // re-fetching history without requiring a manual page refresh.
      const ensuredSessionId = await ensureSessionForSend(modelToUse);
      const effectiveSessionId = ensuredSessionId || sessionId || null;
      const client_message_id = makeClientMessageId();

      // 🔐 LOCK BOT
      setBotStatus("requesting");
      botStartTimeRef.current = Date.now();

      // ---------------- USER MESSAGE ----------------
      const time = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const filesForMessage = filesToUse
        .filter((f) => ["done", "already_trained"].includes(f.status))
        .map((f) => ({
          name: f.file.name,
          size: f.file.size,
          type: f.file.type || getExt(f.file.name) || "unknown",
        }));

      const userMsg = {
        id: Date.now(),
        sender: "user",
        message: finalInput,
        files: filesForMessage,
        time,
        client_message_id,
        userName: user?.user_name || userName || "User",
        userPic: profilePic || loggedInPic,
      };

      setMessages((prev) => [...prev, userMsg]);
      setShouldForceScroll(true);
      setInput("");
      setAttachedFiles([]);

      const textarea = document.querySelector(".input-textarea");
      if (textarea) textarea.style.height = "40px";

      // ---------------- BOT TYPING ----------------
      const botTypingId = Date.now() + 1;
      setMessages((prev) => [
        ...prev,
        {
          id: botTypingId,
          sender: "bot",
          message: "",
          partialMessage: "",
          showDots: true,
          time,
          client_message_id,

          // ⏱️ ADD HERE
          startedAt: Date.now(),
          liveResponseTime: 0,

          // 🔹 NEW (initialize)
          response_time: null,
          input_tokens: null,
          output_tokens: null,
        },
      ]);
      // 🔐 BOT BUSY
      setBotStatus("requesting");
      setIsBotResponding(true);

      const hasDocs = uploadedDocNames.length > 0 || emailFiles.length > 0;
      const knowledgeEnabled = Boolean(
        isKnowledgeSearchActive ||
          selectedGroups.length > 0 ||
          selectedFiles.length > 0,
      );

      try {
        const apiEndpoint = isFullPrivacy
          ? `${process.env.REACT_APP_API_URL}/chat/chatroute`
          : `${process.env.REACT_APP_API_URL}/chat/chatroute`;

        const requestBody = isFullPrivacy
          ? {
              prompt: finalInput,
              model_id: modelToUse,
              session_id: effectiveSessionId,
              mode: selectedMode,
              attached_document,
              client_message_id,
              // search_active: searchActive, // ✅ here
               search_active: isSearchActive,
               // Global KB toggle only (Custom RAG is inferred from selected files/groups)
               knowledge_search_active: isKnowledgeSearchActive,
               knowledge_search_enabled: isKnowledgeSearchActive,
               group_ids: selectedGroups,
               knowledge_base_files: selectedFiles,
              has_docs: hasDocs,
              file_names: uploadedDocNames,
              // ✅ NEW: AI preferences
              preferences: {
                tone: aiPreferences.tone,
                response_length: aiPreferences.length,
                ai_role: aiPreferences.aiRole,
                user_role: aiPreferences.userRole,
              },
            }
          : {
              prompt: finalInput,
              model_id: modelToUse,
              session_id: effectiveSessionId,
              mode: selectedMode,
              attached_document,
              client_message_id,
              // search_active: searchActive, // ✅ here
               search_active: isSearchActive,
               // Global KB toggle only (Custom RAG is inferred from selected files/groups)
               knowledge_search_active: isKnowledgeSearchActive,
               knowledge_search_enabled: isKnowledgeSearchActive,
               group_ids: selectedGroups,
               knowledge_base_files: selectedFiles,
              has_docs: hasDocs,
              file_names: uploadedDocNames,
              // ✅ NEW: AI preferences
              preferences: {
                tone: aiPreferences.tone,
                response_length: aiPreferences.length,
                ai_role: aiPreferences.aiRole,
                user_role: aiPreferences.userRole,
              },
            };

        // const apiEndpoint = isFullPrivacy ? "/chat/save" : "/chat/query";

        // const requestBody = isFullPrivacy
        //   ? {
        //       prompt: finalInput,
        //       model_name: selectedModel,
        //       session_id: sessionId,
        //       mode: selectedMode,
        //       attached_document,
        //     }
        //   : {
        //       query: finalInput,
        //       model_name: selectedModel,
        //       session_id: sessionId,
        //       mode: selectedMode,
        //       attached_document,
        //       search_active: searchActive, // ✅ here
        //     };

        // ✅ AXIOS INSTANCE (REFRESH TOKEN SAFE)
        // ✅ TOKEN ADDED HERE (ONLY CHANGE)

        const useMultipart = emailFiles.length > 0;

        let payloadToSend = requestBody;
        if (useMultipart) {
          const formData = new FormData();
          formData.append("prompt", requestBody.prompt ?? "");
          formData.append("model_id", requestBody.model_id ?? "");

          if (requestBody.session_id != null) {
            formData.append("session_id", requestBody.session_id);
          }
          if (requestBody.mode != null) {
            formData.append("mode", requestBody.mode);
          }

          formData.append(
            "client_message_id",
            String(requestBody.client_message_id || ""),
          );

          formData.append(
            "search_active",
            String(Boolean(requestBody.search_active)),
          );
          formData.append(
            "knowledge_search_active",
            String(Boolean(requestBody.knowledge_search_active)),
          );
          formData.append(
            "knowledge_search_enabled",
            String(Boolean(requestBody.knowledge_search_enabled)),
          );
          formData.append(
            "group_ids",
            JSON.stringify(requestBody.group_ids || []),
          );
          formData.append(
            "knowledge_base_files",
            JSON.stringify(requestBody.knowledge_base_files || []),
          );
          formData.append("has_docs", String(Boolean(requestBody.has_docs)));

          formData.append(
            "file_names",
            JSON.stringify(requestBody.file_names || []),
          );
          formData.append(
            "attached_document",
            JSON.stringify(requestBody.attached_document || []),
          );
          formData.append(
            "preferences",
            JSON.stringify(requestBody.preferences || {}),
          );

          const zipFile = emailFiles.find(
            (f) => getExt(f?.file?.name) === "zip",
          )?.file;
          if (zipFile) formData.append("zipfile", zipFile);

          emailFiles
            .filter((f) => getExt(f?.file?.name) === "msg")
            .forEach((f) => formData.append("msgfiles", f.file));

          payloadToSend = formData;
        }

        // Prefer async job API (fast 202 + poll result). Fall back to legacy
        // blocking endpoint if job API is not available.
        try {
          const jobResponse = await api.post(
            `${process.env.REACT_APP_API_URL}/chat/job`,
            payloadToSend,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          const jobData = jobResponse?.data;
          if (jobResponse?.status === 202 && jobData?.chat_id) {
            recoverBotResponseByChatId({
              chatId: jobData.chat_id,
              targetSessionId: effectiveSessionId,
              userText: finalInput,
              botTypingId,
              startedAt: botStartTimeRef.current,
            });
            return;
          }
        } catch {
          // ignore and fall back
        }

        const response = await api.post(apiEndpoint, payloadToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const botData = response.data;

        const fullResponse =
          botData.response ||
          botData.message ||
          "Apologies, there was a temporary issue processing your request.";

        // console.log("Bot Data:", botData, "session ID", sessionId);

        if (botData.session_id && botData.session_id !== sessionId) {
          skipHistoryFetchRef.current = true; // 🚫 SKIP HISTORY
          setSessionId(botData.session_id);
          dispatch(setReduxSessionId(botData.session_id));
          navigate("/user/new", {
            state: { sessionId: botData.session_id },
            replace: true,
          });
        }

        dispatch(setRefreshSidebar(true));
        startTypingAnimation(fullResponse, botTypingId, botData);
        setUploadedDocNames([]);
        // setBotStatus("idle");
      } catch (error) {
        console.error("Chat API Error:", error);
        // setUploadedDocNames([]);

        // 🛑 Stop typing animation
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }

        // If the request drops (timeout / network), the backend may still finish
        // and save the response. Try to recover via history fetch before showing
        // a final apology.
        isTypingRef.current = false;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId
              ? {
                  ...msg,
                  message:
                    "Connection issue detected. Trying to fetch the response…",
                  partialMessage: "",
                  showDots: true,
                }
              : msg,
          ),
        );

        if (effectiveSessionId) {
          recoverBotResponse({
            targetSessionId: effectiveSessionId,
            userText: finalInput,
            botTypingId,
            startedAt: botStartTimeRef.current,
          });
        } else {
          // No session id available; fall back to previous behavior.
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message:
                      "We apologize for the inconvenience. We’re temporarily unable to process your request due to a service issue on our end. Please try again soon.",
                    partialMessage: "",
                    showDots: false,
                    response_record_time:
                      msg.liveResponseTime?.toFixed(2) ?? "0.00",
                  }
                : msg,
            ),
          );
          setIsBotResponding(false);
          setBotStatus("idle");
          dispatch(setRefreshSidebar(true));
        }
      }
    },
    [
      input,
      selectedModel,
      attachedFiles,
      token,
      user,
      userName,
      profilePic,
      loggedInPic,
      isFullPrivacy,
      sessionId,
      selectedMode,
      navigate,
      dispatch,
      startTypingAnimation,
      clearRecovery,
      ensureSessionForSend,
      recoverBotResponse,
      recoverBotResponseByChatId,
      makeClientMessageId,
    ],
  );

  // ============================================================================
  // MEMOIZED HANDLERS - Wrapped in useCallback to prevent recreation
  // ============================================================================

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast("Text copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, []);

  const isAnyBotTyping = isTypingRef.current;

  const handleRetry = useCallback(
    (botMsgId) => {
      // 🚫 HARD BLOCK
      if (isBotBusy()) {
        showErrorToast(
          "The system is currently generating a response. Please try again shortly.",
        );
        return;
      }

      const botIndex = messages.findIndex((msg) => msg.id === botMsgId);
      if (botIndex === -1) return;

      for (let i = botIndex - 1; i >= 0; i--) {
        if (messages[i].sender === "user") {
          handleSend(messages[i].message);
          break;
        }
      }
    },
    [messages, handleSend],
  );

  const handleStopResponse = useCallback(() => {
    const active = activeResponseRef.current;

    clearRecovery();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }

    isTypingRef.current = false;
    setIsBotResponding(false);
    setBotStatus("idle");

    if (active?.botTypingId && active?.chatId) {
      const partial = String(active.fullResponse || "").slice(
        0,
        Number(active.charIndex || 0),
      );

      // Show only the visible part (ChatGPT-like stop).
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === active.botTypingId
            ? {
                ...msg,
                message: partial,
                partialMessage: "",
                showDots: false,
                response_record_time:
                  msg.response_record_time ??
                  Number((msg.liveResponseTime ?? 0).toFixed(2)),
              }
            : msg,
        ),
      );

      // Persist truncated response + mark stopped (best-effort).
      api
        .post(
          `${process.env.REACT_APP_API_URL}/chat/stop/${active.chatId}`,
          { bot_response: partial },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .catch(() => {});
    } else {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.showDots
            ? {
                ...msg,
                showDots: false,
                response_record_time:
                  msg.response_record_time ??
                  Number((msg.liveResponseTime ?? 0).toFixed(2)),
              }
            : msg,
        ),
      );
    }
    window.speechSynthesis.cancel();
  }, [api, clearRecovery, token]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    dispatch(setIsChatPage(true));
    return () => {
      clearRecovery();
      dispatch(setIsChatPage(false));
    };
  }, [dispatch, clearRecovery]);

  useEffect(() => {
    if (location.state?.sessionId) {
      setSessionId(location.state.sessionId);
    }
  }, [location.state?.sessionId]);

  useEffect(() => {
    if (!chatResetToken) return;

    setMessages([]);
    setInput("");
    setAttachedFiles([]);
    setUploadedDocNames([]);
    setEditingId(null);
    setIsBotResponding(false);
    setBotStatus("idle");
    skipHistoryFetchRef.current = false;
    hasInteractedRef.current = false;

    // If a session id was created, set it; otherwise start fresh (null)
    setSessionId(location.state?.sessionId || null);
  }, [chatResetToken]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      (firstMessage || attachedFilesFromHome?.length > 0) &&
      !hasInteractedRef.current
    ) {
      handleSend(firstMessage, attachedFilesFromHome);
      hasInteractedRef.current = true;
    }
  }, [refreshKey, firstMessage, attachedFilesFromHome, handleSend]);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    const isUserAtBottom =
      chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

    if (shouldForceScroll || isUserAtBottom) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShouldForceScroll(false);
    }
  }, [messages, shouldForceScroll]);

  useEffect(() => {
    if (!sessionId) return;

    // 🚫 SKIP if sessionId came from handleSend
    if (skipHistoryFetchRef.current) {
      skipHistoryFetchRef.current = false; // reset for next time
      return;
    }

    const run = async () => {
      const formatted = await fetchSessionHistory(sessionId);
      if (Array.isArray(formatted)) setMessages(formatted);
    };

    run();
  }, [sessionId, fetchSessionHistory]);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    chatBox.addEventListener("scroll", debouncedScrollCheck);
    return () => chatBox.removeEventListener("scroll", debouncedScrollCheck);
  }, [debouncedScrollCheck]);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/user-profile/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.ok) throw new Error("Failed to fetch profile");
        const data = await res.json();
        setUser(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [refreshKey, token]);

  // Derived state
  const isUploading = useMemo(
    () => attachedFiles.some((f) => f.status === "uploading"),
    [attachedFiles],
  );
  const handleRelatedQuestionSend = useCallback(
    (question) => {
      if (!question || isBotResponding || isAnyBotTyping) return;

      handleSend(question); // ✅ direct send
    },
    [handleSend, isBotResponding, isAnyBotTyping],
  );

  // console.log("uploadedDocNames", uploadedDocNames);

  // ============================================================================
  // RENDER
  // ============================================================================

  // local temp state
  const [localPrefs, setLocalPrefs] = useState(aiPreferences);

  useEffect(() => {
    if (showResponsesModal) {
      setLocalPrefs(aiPreferences);
    }
  }, [showResponsesModal, aiPreferences]);

  const handleChange = (key, value) => {
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApply = () => {
    setAiPreferences(localPrefs);
    setShowResponsesModal(false);
  };

  const handleCancel = () => {
    setLocalPrefs(aiPreferences);
    setShowResponsesModal(false);
  };

  useEffect(() => {
    const close = (e) => {
      if (
        !e.target.closest(".custom-popover") &&
        !e.target.closest(".icon-image")
      ) {
        setShowResponsesModal(false);
      }
    };

    document.addEventListener("click", close, true);
    return () => document.removeEventListener("click", close, true);
  }, []);

  const LENGTH_OPTIONS = [
    { value: "long", label: "Longest (Default)" },
    { value: "deep", label: "Deep Analysis" },
    { value: "medium", label: "Medium" },
  ];

  const AI_ROLE_OPTIONS = [
    { value: "default", label: "General Assistant (Default)" },
    { value: "Brainstorm partner", label: "Brainstorm partner" },
    { value: "Career coach", label: "Career coach" },
    { value: "Chef", label: "Chef" },
    { value: "Coding coach", label: "Coding coach" },
    { value: "Editor", label: "Editor" },
    { value: "Entertainment guide", label: "Entertainment guide" },
    { value: "Fitness trainer", label: "Fitness trainer" },
    { value: "Gardener", label: "Gardener" },
    { value: "Homework helper", label: "Homework helper" },
    { value: "Language tutor", label: "Language tutor" },
    { value: "Life coach", label: "Life coach" },
    { value: "Marketer", label: "Marketer" },
    { value: "Editor", label: "Editor" },
    { value: "Product manager", label: "Product manager" },
    { value: "Public speaking coach", label: "Public speaking coach" },
    { value: "Social media copywriter", label: "Social media copywriter" },
    { value: "Storyteller", label: "Storyteller" },
    { value: "Tech support specialist", label: "Tech support specialist" },
    { value: "Teacher", label: "Teacher" },
    { value: "Translator", label: "Translator" },
    { value: "Travel guide", label: "Travel guide" },
    { value: "Trivia expert", label: "Trivia expert" },
    { value: "Writer", label: "Writer" },
  ];

  const USER_ROLE_OPTIONS = [
    { value: "default", label: "Universal (Default)" },
    { value: "Parent", label: "Parent" },
    { value: "Professional", label: "Professional" },
    { value: "Programmer", label: "Programmer" },
    { value: "Student", label: "Student" },
    { value: "Writer", label: "Writer" },
  ];

  const TONE_MODE_MAP = [
    { value: "default", label: "Neutral (Default)" },
    { value: "eli5", label: "Simple" },
    { value: "tldr", label: "Summary" },
    { value: "jargonize", label: "Professional" },
    { value: "humanize", label: "Naturally" },
    { value: "feynman", label: "Deep" },
  ];

  const DEEP_ANALYSIS_MAP = [
    { value: "deep", label: "Deep Analysis (Default)" },
    { value: "quick", label: "Quick" },
    { value: "balanced", label: "Standard" },
  ];

  return (
    <div className="w-100 h-100 overflow-hidden chat-wrapper">
      <div className="flex-grow-1  overflow-auto chat-box">
        {loading ? (
          <p className="text-center refresh-prompts mt-3">
            Loading messages...
          </p>
        ) : filteredMessages.length === 0 ? (
          <p className="text-center refresh-prompts mt-3">No results found.</p>
        ) : (
          filteredMessages.map((msg) => (
            <ChatMessage
              key={msg.id}
              msg={msg}
              loggedInPic={loggedInPic}
              userName={user?.user_name || userName}
              fontSize={fontSizeMap[chatPreferences.font_size] || "1rem"}
              onCopy={handleCopy}
              onRetry={handleRetry}
              onTextToSpeech={handleTextToSpeech}
              onRelatedQuestionSend={handleRelatedQuestionSend}
              searchActive={isSearchActive}
              // 🔹 ADD THIS
              onUpdateMessage={updateBotMessage}
              botBusy={botBusy} // ✅ ADD THIS
              selectedKbFiles={selectedFiles}
              onToggleKbFile={toggleKbFile}
              onKbNewChat={createNewChatFromKb}
            />
          ))
        )}

        <div ref={chatEndRef} />
      </div>

      {chatPreferences.auto_scroll && showScrollDown && (
        <button
          className="scroll-down-btn m-auto"
          onClick={() =>
            chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
          }
        >
          <i className="bi bi-arrow-down" style={{ fontSize: "24px" }}></i>
        </button>
      )}

      <div className="">
        <div
          className="rounded p-1 mt-1 input-area"
          style={{ border: "1px solid var(--dark-color)" }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.doc,.pptx,.xlsx,.zip,.msg,.mp3,.wav,.aac,.flac,.mp4,.mov,.avi,.mkv,.flv,.png,.jpg,.jpeg,.gif,.bmp,.tiff"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {attachedFiles.length > 0 && (
            <div className="d-flex flex-wrap mb-2">
              {attachedFiles.map((item, idx) => (
                <div
                  key={idx}
                  className="attached-file-chip position-relative me-2 mt-1 rounded bg-light d-flex flex-column px-2 py-1"
                  style={{
                    maxWidth: "200px",
                    overflow: "visible",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    <img src={fileIcon} alt="File" width="18" />
                    <span
                      style={{
                        fontSize: "small",
                        display: "inline-block",
                        maxWidth: "180px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                      title={item.file.name}
                    >
                      {item.file.name}
                    </span>
                  </div>

                  <small style={{ fontSize: "10px", color: "#555" }}>
                    {item.file.size < 1024 * 1024
                      ? `${(item.file.size / 1024).toFixed(1)} KB`
                      : `${(item.file.size / (1024 * 1024)).toFixed(2)} MB`}
                  </small>

                  {item.status === "done" && (
                    <small style={{ fontSize: "10px", color: "green" }}>
                      Uploaded ✅
                    </small>
                  )}
                  {item.status === "error" && (
                    <small style={{ fontSize: "10px", color: "red" }}>
                      Failed ❌
                    </small>
                  )}
                  {item.status === "already_trained" && (
                    <small style={{ fontSize: "10px", color: "green" }}>
                      Uploaded ✅{/* Training Completed ✅ */}
                    </small>
                  )}

                  <img
                    src={crossIcon}
                    alt="Remove"
                    onClick={() => removeAttachedFile(idx)}
                    style={{
                      position: "absolute",
                      top: "-2px",
                      right: "-6px",
                      width: "14px",
                      height: "14px",
                      zIndex: 1003,
                      cursor: "pointer",
                    }}
                  />

                  {item.status === "uploading" && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(255, 255, 255, 0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "inherit",
                        zIndex: 2,
                      }}
                    >
                      <div
                        className="spinner-border"
                        role="status"
                        style={{
                          width: "20px",
                          height: "20px",
                          color: "var(--dark-color)",
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mb-2">
            <textarea
              className="p-2 pb-4 border-0 shadow-none input-textarea"
              placeholder={
                hasEmailExtractFiles
                  ? "Attachments received for email extraction. You can add more files. Text input is disabled."
                  : isFocused
                    ? ""
                    : branding("Welcome back to VelaKoda! How can I assist you?")
              }
              value={input}
              disabled={hasEmailExtractFiles}
              readOnly={hasEmailExtractFiles}
              onChange={(e) => {
                if (hasEmailExtractFiles) return;
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
                if (e.target.scrollHeight > 200) {
                  e.target.style.overflowY = "auto";
                } else {
                  e.target.style.overflowY = "hidden";
                }
              }}
              rows={1}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                if (hasEmailExtractFiles) {
                  e.preventDefault();
                  return;
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!isBotResponding) {
                    handleSend();
                    e.target.style.height = "40px";
                    e.target.style.overflowY = "hidden";
                    e.target.style.height = "auto";
                  }
                }
              }}
            />
          </div>

          <div className="d-flex align-items-center justify-content-between rounded-3 p-1">
            <div className="d-flex gap-2">
              {/* Customize Responses */}
              <div className="position-relative">
                <button
                  className="btn p-0 icon-image mb-1"
                  title="Customize Responses"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowResponsesModal((prev) => !prev);
                  }}
                >
                  <i className="bi bi-sliders fs-6"></i>
                </button>

                {showResponsesModal && (
                  <div
                    className="custom-popover ak-text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="popover-content">
                      <div className="fw-bold mb-2  ak-text-md">
                        Customize Responses
                      </div>

                      <div className="settings-body">
                        {/* Tool Mode */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Tone / Style
                          </span>
                          <select
                            value={localPrefs.tone}
                            onChange={(e) =>
                              handleChange("tone", e.target.value)
                            }
                          >
                            {TONE_MODE_MAP.map((tone) => (
                              <option key={tone.value} value={tone.value}>
                                {tone.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* Analysis */}
                        {/* <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Analysis
                          </span>
                          <select
                            value={localPrefs.length}
                            onChange={(e) =>
                              handleChange("analysis", e.target.value)
                            }
                          >
                            {DEEP_ANALYSIS_MAP.map((length) => (
                              <option key={length.value} value={length.value}>
                                {length.label}
                              </option>
                            ))}
                          </select>
                        </div> */}

                        {/* Length */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Length of responses
                          </span>
                          <select
                            value={localPrefs.length}
                            onChange={(e) =>
                              handleChange("length", e.target.value)
                            }
                          >
                            {LENGTH_OPTIONS.map((length) => (
                              <option key={length.value} value={length.value}>
                                {length.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* AI Role */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            AI role
                          </span>
                          <select
                            value={localPrefs.aiRole}
                            onChange={(e) =>
                              handleChange("aiRole", e.target.value)
                            }
                          >
                            {AI_ROLE_OPTIONS.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* User Role */}
                        <div className="settings-row">
                          <span className="refresh-prompts ak-text-sm">
                            Your role
                          </span>
                          <select
                            value={localPrefs.userRole}
                            onChange={(e) =>
                              handleChange("userRole", e.target.value)
                            }
                          >
                            {USER_ROLE_OPTIONS.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="d-flex gap-2 justify-content-center mt-2 align-items-center">
                        <button
                          className="cancle-btn w-25 ak-text-sm py-1"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                        <button
                          className="custom-btn w-25 ak-text-sm py-1"
                          onClick={handleApply}
                        >
                          Apply
                        </button>
                        <span
                          className="reset-icon"
                          title="Reset to default"
                          onClick={() => {
                            // resetAiPreferences();
                            setLocalPrefs(DEFAULT_AI_PREFERENCES);
                            // setShowResponsesModal(false);
                          }}
                        >
                          <img
                            src={reloadIcon}
                            className="icon-image pointer"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Attach */}
              <button
                className=" btn p-0 icon-image"
                title="Attach Files"
                onClick={handleAttachClick}
              >
                <i className="bi bi-paperclip fs-5"></i>
              </button>

              {/* Read Aloud */}
              <button
                className=" btn p-0 icon-image"
                onClick={toggleReadAloud}
                title="Reader"
              >
                <i
                  className={`bi ${
                    isSpeaking ? "bi-volume-mute" : "bi-volume-up"
                  } fs-5`}
                ></i>
                {/* <span className="pill-label">Reader</span> */}
              </button>

              {/* Mic */}
              <button
                className=" btn p-0 icon-image"
                onClick={() => {
                  setMicOpen(true);
                  startRecording();
                }}
                title="Audio"
              >
                <i className="bi bi-mic fs-5"></i>
                {/* <span className="pill-label">Audio</span> */}
              </button>

              {/* 🔴 YOUR MIC OVERLAY — UNCHANGED */}
              {micOpen && (
                <div className="mic-overlay position-absolute start-50 translate-middle-x p-3">
                  {/* Waveform */}
                  <div className="mic-waveform d-flex gap-2 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="wave-dot"></span>
                    ))}
                  </div>

                  {/* Live preview */}
                  <div
                    className="live-preview mb-2 p-2 rounded"
                    style={{
                      background: "#f8f9fa",
                      height: "40px",
                      fontSize: "12px",
                      overflowY: "auto",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {isRecording
                      ? "Listening..."
                      : isProcessingSTT
                        ? "Processing..."
                        : interimTranscript}
                  </div>

                  {/* Buttons */}
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm d-flex align-items-center justify-content-center"
                      onClick={stopRecording}
                      disabled={isProcessingSTT}
                      title="Confirm"
                      style={{
                        color: "var(--btn-color)",
                        backgroundColor: "var(--primary-color)",
                        minWidth: "36px",
                        opacity: isProcessingSTT ? 0.7 : 1,
                      }}
                    >
                      {isProcessingSTT ? (
                        <span
                          className="spinner-border spinner-border-sm text-light"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="bi bi-check-lg" />
                      )}
                    </button>

                    <button
                      className="btn btn-sm border"
                      onClick={cancelRecording}
                      disabled={isProcessingSTT}
                      title="Cancel"
                      style={{
                        color: "var(--primary-color)",
                        backgroundColor: "var(--btn-color)",
                        opacity: isProcessingSTT ? 0.6 : 1,
                      }}
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* SEND BUTTON — UNCHANGED */}
            <button
              className="custom-send-btn d-flex align-items-center justify-content-center"
              onClick={
                isBotResponding ? handleStopResponse : () => handleSend()
              }
              disabled={isUploading}
              style={{
                opacity: isUploading ? 0.5 : 1,
                cursor: isUploading ? "not-allowed" : "pointer",
              }}
            >
              {isBotResponding ? (
                <i
                  className="bi bi-stop-fill rounded-circle d-flex align-items-center justify-content-center text-white"
                  title="Stop"
                  style={{
                    width: "30px",
                    height: "27px",
                    background: "#3e3e3e",
                    border: "none",
                  }}
                ></i>
              ) : (
                <img src={send} alt="Send" title="Send" />
              )}
            </button>
          </div>
        </div>
        <span className="claimer-text d-flex align-items-center justify-content-center w-100 text-center px-0 px-md-3 py-0 py-md-1">
          VelaKoda may display inaccurate information or can make mistakes.
        </span>
      </div>

      {errorModal.show && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog-centered">
            <div className="modal-content mx-auto">
              <div className="modal-header border-bottom justify-content-center position-relative">
                <h5 className="modal-title m-0">File Size Alert</h5>
                <button
                  type="button"
                  className="modal-close-btn"
                  aria-label="Close"
                  onClick={handleCancel}
                >
                  <img src={crossIcon} className="icon-image" alt="Close" />
                </button>
              </div>
              <div className="modal-body text-center">
                <p
                  dangerouslySetInnerHTML={{ __html: errorModal.message }}
                  className="fw-normal"
                  style={{ lineHeight: "1.6", fontSize: "15px" }}
                ></p>
              </div>

              <div className="d-flex justify-content-center mb-3 gap-3">
                <button
                  type="button"
                  className="w-25 cancle-btn"
                  onClick={handleCancel}
                >
                  No
                </button>
                <button
                  type="button"
                  className="w-25 custom-btn"
                  onClick={handleOk}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
